import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Field = ({ label, value, onChange, textarea, type = "text" }) => (
  <div>
    <label className="text-xs text-[#525252] uppercase tracking-wider block mb-1.5">{label}</label>
    {textarea ? (
      <textarea className="field-dark min-h-[70px]" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} className="field-dark" value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} />
    )}
  </div>
);

const SingletonForm = ({ contentKey, fields, testId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/content").then((r) => setData(r.data[contentKey] || {}));
  }, [contentKey]);
  const save = async () => {
    await api.put(`/admin/content/${contentKey}`, data);
    toast.success("Kaydedildi — site anında güncellendi");
  };
  if (!data) return <p className="text-[#525252]">Yükleniyor...</p>;
  return (
    <div className="space-y-4 max-w-2xl" data-testid={testId}>
      {fields.map(([key, label, opts = {}]) => (
        <Field key={key} label={label} value={data[key]} onChange={(v) => setData((d) => ({ ...d, [key]: v }))} {...opts} />
      ))}
      <button data-testid={`${testId}-save`} onClick={save} className="flex items-center gap-2 bg-[#8B0000] hover:bg-[#C1121F] px-5 py-2.5 text-xs tracking-wider uppercase transition-colors">
        <Save size={14} /> Kaydet
      </button>
    </div>
  );
};

const CollectionEditor = ({ coll, fields, label }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/admin/items/${coll}`).then((r) => setItems(r.data)), [coll]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const blank = Object.fromEntries(fields.map(([k, , opts]) => [k, opts?.type === "number" ? 0 : ""]));
    blank.order = items.length + 1;
    const { data } = await api.post(`/admin/items/${coll}`, blank);
    setItems((l) => [...l, data]);
  };
  const save = async (item) => {
    await api.put(`/admin/items/${coll}/${item.id}`, item);
    toast.success("Kaydedildi");
  };
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/items/${coll}/${id}`);
    setItems((l) => l.filter((i) => i.id !== id));
    toast.success("Silindi");
  };
  const toggleVisible = async (item) => {
    const updated = { ...item, visible: item.visible === false };
    await api.put(`/admin/items/${coll}/${item.id}`, updated);
    setItems((l) => l.map((i) => (i.id === item.id ? updated : i)));
  };
  const setField = (id, key, val) => setItems((l) => l.map((i) => (i.id === id ? { ...i, [key]: val } : i)));

  return (
    <div data-testid={`editor-${coll}`}>
      <button data-testid={`add-${coll}-btn`} onClick={add} className="flex items-center gap-2 border border-[#262626] bg-[#141414] px-4 py-2 text-xs tracking-wider uppercase hover:border-[#8B0000] transition-colors mb-5">
        <Plus size={14} /> {label} Ekle
      </button>
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="border border-[#232323] bg-[#111111] p-5">
            <div className="grid md:grid-cols-2 gap-3">
              {fields.map(([key, flabel, opts = {}]) => (
                <div key={key} className={opts.textarea ? "md:col-span-2" : ""}>
                  <Field label={flabel} value={item[key]} onChange={(v) => setField(item.id, key, v)} {...opts} />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => save(item)} className="flex items-center gap-1.5 bg-[#8B0000] hover:bg-[#C1121F] px-3 py-1.5 text-xs uppercase tracking-wider transition-colors"><Save size={13} /> Kaydet</button>
              <button onClick={() => toggleVisible(item)} className="flex items-center gap-1.5 border border-[#262626] px-3 py-1.5 text-xs uppercase tracking-wider text-[#A3A3A3] hover:border-[#8B0000] transition-colors">
                {item.visible === false ? <><EyeOff size={13} /> Gizli</> : <><Eye size={13} /> Yayında</>}
              </button>
              <button onClick={() => remove(item.id)} className="ml-auto text-[#525252] hover:text-[#C1121F]"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ContentPage() {
  return (
    <div data-testid="admin-content">
      <h1 className="text-2xl font-bold mb-8">İçerik Yönetimi</h1>
      <Tabs defaultValue="stats">
        <TabsList className="bg-[#141414] border border-[#232323] rounded-none flex-wrap h-auto">
          {[["stats", "Sayaçlar"], ["brands", "İş Birlikleri"], ["services", "Hizmetler"], ["timeline", "Timeline"], ["press", "Basın"], ["music", "Müzik"], ["texts", "Metinler"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none data-[state=active]:bg-[#8B0000] data-[state=active]:text-white text-xs">{l}</TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-8">
          <TabsContent value="stats">
            <SingletonForm contentKey="stats" testId="stats-form" fields={[
              ["tiktok_followers", "TikTok Takipçi Sayısı", { type: "number" }],
              ["tiktok_likes", "TikTok Beğeni Sayısı", { type: "number" }],
              ["instagram_followers", "Instagram Takipçi Sayısı", { type: "number" }],
              ["youtube_subs", "YouTube Abone Sayısı", { type: "number" }],
            ]} />
          </TabsContent>
          <TabsContent value="brands">
            <CollectionEditor coll="brands" label="Marka" fields={[
              ["name", "Marka Adı"], ["campaign", "Kampanya Adı"], ["metric", "Sonuç Metriği"],
              ["image", "Görsel URL (Medya'dan kopyala)"], ["order", "Sıra", { type: "number" }],
              ["description", "Açıklama", { textarea: true }],
            ]} />
          </TabsContent>
          <TabsContent value="services">
            <CollectionEditor coll="services" label="Hizmet" fields={[
              ["title", "Başlık"], ["icon", "İkon (lucide adı: Video, Music...)"],
              ["order", "Sıra", { type: "number" }], ["description", "Açıklama", { textarea: true }],
            ]} />
          </TabsContent>
          <TabsContent value="timeline">
            <CollectionEditor coll="timeline" label="Durak" fields={[
              ["year", "Yıl"], ["title", "Başlık"], ["image", "Görsel URL"],
              ["order", "Sıra", { type: "number" }], ["text", "Metin", { textarea: true }],
            ]} />
          </TabsContent>
          <TabsContent value="press">
            <CollectionEditor coll="press" label="Basın" fields={[
              ["name", "Yayın Adı"], ["url", "Link"], ["order", "Sıra", { type: "number" }],
            ]} />
          </TabsContent>
          <TabsContent value="music">
            <SingletonForm contentKey="music" testId="music-form" fields={[
              ["video_url", "YouTube Embed URL"], ["title", "Şarkı Başlığı"], ["views", "İzlenme Metni"],
            ]} />
          </TabsContent>
          <TabsContent value="texts">
            <SingletonForm contentKey="texts" testId="texts-form" fields={[
              ["hero_tagline", "Hero Tagline"], ["hero_marquee", "Hero Marquee Metni"],
              ["hero_image", "Hero Portre Görsel URL"],
              ["stats_subline", "İstatistik Alt Satırı", { textarea: true }],
              ["story_title", "Hikaye Başlığı"], ["collab_title", "İş Birlikleri Başlığı"],
              ["family_title", "Aile Başlığı"], ["family_quote", "Viral Slogan", { textarea: true }],
              ["family_body", "Aile Metni", { textarea: true }], ["family_message", "Aile Mesajı", { textarea: true }],
              ["music_title", "Müzik Başlığı"], ["music_note", "Müzik Notu", { textarea: true }],
              ["services_title", "Hizmetler Başlığı"], ["press_title", "Basın Başlığı"], ["contact_title", "İletişim Başlığı"],
            ]} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
